import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { PageHeader } from '../../../shared/components/PageHeader';
import { EmptyState } from '../../../shared/components/EmptyState';
import { ErrorAlert } from '../../../shared/components/ErrorAlert';
import { Spinner } from '../../../shared/components/Spinner';
import { ConfirmModal } from '../../../shared/components/ConfirmModal';
import { useDocumentTitle } from '../../../shared/hooks/useDocumentTitle';
import { useAuth } from '../../../contexts/AuthContext';
import type {
  FeatureFlag,
  FeatureFlagCreatePayload,
  FeatureFlagFilters,
  FeatureFlagSortKey,
  FeatureFlagUpdatePayload,
  SortDirection,
} from '../../../models/featureFlag';
import {
  createFeatureFlag,
  fetchFeatureFlags,
  filterFeatureFlags,
  logFeatureFlagAudit,
  sortFeatureFlags,
  updateFeatureFlag,
} from '../../../services/admin/featureFlagService';
import { FeatureFlagTable } from '../components/FeatureFlagTable';
import { FeatureFlagFormModal } from '../components/FeatureFlagFormModal';
import { FeatureFlagAssignmentsPanel } from '../components/FeatureFlagAssignmentsPanel';
import { FeatureFlagAuditDrawer } from '../components/FeatureFlagAuditDrawer';

const STATUS_OPTIONS = ['all', 'active', 'archived'];

/** Feature flags shown per page. */
const PAGE_SIZE = 10;

export function FeatureFlagListPage(): React.ReactElement {
  useDocumentTitle('Feature flags · Triplicity Backoffice');
  const { getAccessToken, isAuthenticated } = useAuth();

  const [flags, setFlags] = useState<FeatureFlag[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [filters, setFilters] = useState<FeatureFlagFilters>({ status: 'active' });
  const [sortKey, setSortKey] = useState<FeatureFlagSortKey>('key');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [page, setPage] = useState(1);

  const [formVisible, setFormVisible] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit'>('create');
  const [editingFlag, setEditingFlag] = useState<FeatureFlag | null>(null);

  const [confirm, setConfirm] = useState<{ kind: 'archive'; flag: FeatureFlag } | null>(null);
  const [managingFlag, setManagingFlag] = useState<FeatureFlag | null>(null);
  const [auditingFlag, setAuditingFlag] = useState<FeatureFlag | null>(null);

  const reload = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const rows = await fetchFeatureFlags(getAccessToken, filters);
      setFlags(rows);
    } catch (err) {
      setFlags([]);
      setError(err instanceof Error ? err.message : 'Failed to load feature flags.');
    } finally {
      setLoading(false);
    }
  }, [getAccessToken, filters]);

  useEffect(() => {
    logFeatureFlagAudit('access', { route: '/admin/feature-flags', authenticated: isAuthenticated });
    void reload();
  }, [reload, isAuthenticated]);

  const filteredFlags = useMemo(() => filterFeatureFlags(flags, filters), [flags, filters]);
  const sortedFlags = useMemo(
    () => sortFeatureFlags(filteredFlags, sortKey, sortDirection),
    [filteredFlags, sortKey, sortDirection]
  );

  const totalPages = Math.max(1, Math.ceil(sortedFlags.length / PAGE_SIZE));

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const pageFlags = useMemo(
    () => sortedFlags.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [sortedFlags, page]
  );

  const rangeStart = sortedFlags.length === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(page * PAGE_SIZE, sortedFlags.length);

  const handleSort = (key: FeatureFlagSortKey): void => {
    if (sortKey === key) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDirection('asc');
    }
    setPage(1);
  };

  const handleFilterChange = (next: FeatureFlagFilters): void => {
    logFeatureFlagAudit('filter', { filters: next });
    setFilters(next);
    setPage(1);
  };

  const openCreate = (): void => {
    setFormMode('create');
    setEditingFlag(null);
    setStatusMessage(null);
    setFormVisible(true);
  };

  const openEdit = (flag: FeatureFlag): void => {
    setFormMode('edit');
    setEditingFlag(flag);
    setStatusMessage(null);
    setFormVisible(true);
  };

  const handleCreate = async (payload: FeatureFlagCreatePayload): Promise<void> => {
    await createFeatureFlag(payload, getAccessToken);
    logFeatureFlagAudit('create', { key: payload.key });
    setStatusMessage(`Feature flag “${payload.key}” created.`);
    await reload();
  };

  const handleUpdate = async (id: string, payload: FeatureFlagUpdatePayload): Promise<void> => {
    await updateFeatureFlag(id, payload, getAccessToken);
    logFeatureFlagAudit('update', { id });
    setStatusMessage('Feature flag updated.');
    await reload();
  };

  const handleToggleEnabled = async (flag: FeatureFlag): Promise<void> => {
    await updateFeatureFlag(flag.id, { enabledGlobally: !flag.enabledGlobally }, getAccessToken);
    logFeatureFlagAudit('update', { id: flag.id, enabledGlobally: !flag.enabledGlobally });
    setStatusMessage(`Feature flag “${flag.key}” ${flag.enabledGlobally ? 'disabled' : 'enabled'}.`);
    await reload();
  };

  const handleToggleArchived = async (flag: FeatureFlag): Promise<void> => {
    if (!flag.isArchived) {
      setConfirm({ kind: 'archive', flag });
      return;
    }
    await updateFeatureFlag(flag.id, { isArchived: false }, getAccessToken);
    logFeatureFlagAudit('archive', { id: flag.id, isArchived: false });
    setStatusMessage(`Feature flag “${flag.key}” unarchived.`);
    await reload();
  };

  const handleConfirm = async (): Promise<void> => {
    if (!confirm) {
      return;
    }
    const { flag } = confirm;
    await updateFeatureFlag(flag.id, { isArchived: true }, getAccessToken);
    logFeatureFlagAudit('archive', { id: flag.id, isArchived: true });
    setStatusMessage(`Feature flag “${flag.key}” archived.`);
    setConfirm(null);
    await reload();
  };

  return (
    <div>
      <PageHeader
        title="Feature flags"
        subtitle="Enable or disable platform functionality without a deployment."
        actions={
          <button type="button" className="bo-btn bo-btn-primary" onClick={openCreate}>
            New feature flag
          </button>
        }
      />

      <div className="bo-admin-toolbar bo-card">
        <label className="bo-field" htmlFor="feature-flag-status">
          <span className="bo-field-label">Status</span>
          <select
            id="feature-flag-status"
            className="bo-input"
            value={filters.status ?? 'active'}
            onChange={(event) => handleFilterChange({ ...filters, status: event.target.value })}
          >
            {STATUS_OPTIONS.map((option) => (
              <option key={option} value={option}>
                {option === 'all' ? 'All' : option.charAt(0).toUpperCase() + option.slice(1)}
              </option>
            ))}
          </select>
        </label>
        <label className="bo-field" htmlFor="feature-flag-search">
          <span className="bo-field-label">Search</span>
          <input
            id="feature-flag-search"
            className="bo-input"
            placeholder="Key or description"
            value={filters.search ?? ''}
            onChange={(event) => handleFilterChange({ ...filters, search: event.target.value })}
          />
        </label>
      </div>

      {statusMessage ? (
        <p className="bo-form-success" role="status">
          {statusMessage}
        </p>
      ) : null}

      {loading ? (
        <div className="bo-card bo-detail-loading">
          <Spinner label="Loading feature flags…" />
        </div>
      ) : null}

      {!loading && error ? <ErrorAlert message={error} /> : null}

      {!loading && !error && filteredFlags.length === 0 ? (
        <EmptyState
          title="No feature flags"
          description="Create a feature flag to control platform functionality without a deployment."
          action={
            <button type="button" className="bo-btn bo-btn-primary" onClick={openCreate}>
              New feature flag
            </button>
          }
        />
      ) : null}

      {!loading && !error && filteredFlags.length > 0 ? (
        <>
          <FeatureFlagTable
            flags={pageFlags}
            sortKey={sortKey}
            sortDirection={sortDirection}
            onSort={handleSort}
            onEdit={openEdit}
            onToggleEnabled={(flag) => void handleToggleEnabled(flag)}
            onToggleArchived={(flag) => void handleToggleArchived(flag)}
            onManageAssignments={(flag) => setManagingFlag(flag)}
            onViewAudit={(flag) => setAuditingFlag(flag)}
          />
          <div className="bo-admin-pagination">
            <span className="bo-muted">
              Showing {rangeStart}–{rangeEnd} of {sortedFlags.length}
            </span>
            <div className="bo-admin-pagination-controls">
              <button
                type="button"
                className="bo-btn bo-btn-secondary"
                onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                disabled={page <= 1}
              >
                Previous
              </button>
              <span className="bo-muted">
                Page {page} of {totalPages}
              </span>
              <button
                type="button"
                className="bo-btn bo-btn-secondary"
                onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
                disabled={page >= totalPages}
              >
                Next
              </button>
            </div>
          </div>
        </>
      ) : null}

      <FeatureFlagFormModal
        visible={formVisible}
        mode={formMode}
        initial={editingFlag}
        onClose={() => setFormVisible(false)}
        onCreate={handleCreate}
        onUpdate={handleUpdate}
      />

      <ConfirmModal
        visible={confirm !== null}
        title="Archive feature flag"
        message={
          confirm ? `Archive “${confirm.flag.key}”? It will be hidden from the active list and disabled everywhere.` : ''
        }
        confirmLabel="Archive"
        confirmVariant="danger"
        onClose={() => setConfirm(null)}
        onConfirm={handleConfirm}
      />

      <FeatureFlagAssignmentsPanel
        visible={managingFlag !== null}
        flag={managingFlag}
        onClose={() => setManagingFlag(null)}
        onChanged={() => void reload()}
      />

      <FeatureFlagAuditDrawer
        visible={auditingFlag !== null}
        flag={auditingFlag}
        onClose={() => setAuditingFlag(null)}
      />
    </div>
  );
}
